import React, { useEffect, useState } from "react";
import { Button, Card, CardBody, CardHeader, Col, Form, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { useApiStore } from "../../hooks/ApiStoreContext";
import Swal from "sweetalert2";

function AddUser() {
  const apiStore = useApiStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [heading, setHeading] = useState("Add");
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]); // Store all users
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isValidating },
    reset,
    setValue,
    getValues,
  } = useForm({
    mode: "onBlur", // Validate on blur
    defaultValues: {
      userId: 0,
      firstName: "",
      lastName: "",
      email: "",
      contact: "",
      loginId: "",
      password: "",
      roleId: null,
      isActive: true,
    },
  });

  const handleBack = (e) => {
    e.preventDefault();
    navigate("/user");
  };

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  const onPasswordMouseDown = () => {
    setPasswordVisible(true);
  };

  const onPasswordMouseUp = () => {
    setPasswordVisible(false);
  };

  // Validation functions using stored users data
  const validateEmail = (email, userId) => {
    if (!email) return true;
    const exists = users.some((user) => user.email.toLowerCase() === email.toLowerCase() && user.userId !== userId);
    return !exists || "Email is already taken.";
  };

  const validateContact = (contact, userId) => {
    if (!contact) return true;
    const exists = users.some((user) => user.contact === contact && user.userId !== userId);
    return !exists || "Contact number is already taken.";
  };

  const validateLoginId = (loginId, userId) => {
    if (!loginId) return true;
    const exists = users.some((user) => user.loginId.toLowerCase() === loginId.toLowerCase() && user.userId !== userId);
    return !exists || "Login ID is already taken.";
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([fetchRoles(), fetchUsers()]);
        const id = location.pathname.split("/")[2];
        if (!id) {
          setHeading("Add");
        } else {
          setHeading("Edit");
          setValue("userId", parseInt(id));
          await fetchUserData(id);
        }
      } catch (error) {
        console.error("Error fetching initial data:", error);
        Swal.fire({ icon: "error", title: "Error", text: "Failed to load initial data." });
      }
    };
    fetchData();
  }, [location, setValue]);

  const fetchRoles = async () => {
    try {
      const response = await apiStore.get("/roles");  // Adjust the API endpoint accordingly
      setRoles(response);
    } catch (error) {
      if (error.response?.status !== 401) {
        console.error("Error fetching roles:", error);
        Swal.fire({ icon: "error", title: "Error", text: "Failed to load roles." });
      }
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await apiStore.get("/users");
      setUsers(response);
    } catch (error) {
      if (error.response?.status !== 401) {
        console.error("Error fetching users:", error);
        Swal.fire({ icon: "error", title: "Error", text: "Failed to load users data." });
      }
    }
  };

  const fetchUserData = async (id) => {
    try {
      const response = await apiStore.get(`/users/${id}`);
      setValue("firstName", response.firstName);
      setValue("lastName", response.lastName);
      setValue("email", response.email);
      setValue("contact", response.contact);
      setValue("loginId", response.loginId); // Fetch LoginId
      setValue("password", response.password);
      setValue("roleId", response.roleId);
      setValue("isActive", response.isActive);
    } catch (error) {
      if (error.response?.status !== 401) {
        console.error("Error fetching user:", error);
        Swal.fire({ icon: "error", title: "Error", text: "Failed to load user data." });
      }
    }
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const payload = {
        userId: data.userId,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        contact: data.contact,
        loginId: data.loginId, // Include LoginId
        password: data.password || null, // Send null if password is empty for updates
        roleId: parseInt(data.roleId),
        isActive: data.isActive,
      };
      if (!data.userId) {
        await apiStore.post("/users", payload);
        await Swal.fire({ title: "Success", text: "User saved successfully!", icon: "success", confirmButtonText: "OK" });
      } else {
        await apiStore.put(`/users/${data.userId}`, payload);
        await Swal.fire({ title: "Updated", text: "User updated successfully!", icon: "success", confirmButtonText: "OK" });
      }

      navigate("/user");
      resetForm();
    } catch (error) {
      if (error.response?.status !== 401) {
        console.error("Error saving user:", error);
        let errorMessage = "There was an error saving the user.";
        if (error.response?.data?.detail) {
          errorMessage = error.response.data.detail; // Handle duplicate errors
        }
        await Swal.fire({ icon: "error", title: "Error", text: errorMessage });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "This will reset all changes.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, reset",
    }).then((result) => {
      if (result.isConfirmed) {
        resetForm();
      }
    });
  };

  const resetForm = () => {
    reset({
      userId: 0,
      firstName: "",
      lastName: "",
      email: "",
      contact: "",
      loginId: "",
      password: "",
      roleId: null,
      isActive: true,
    });
  };

  return (
    <Row>
      <Col md={12}>
        <Card className="mb-4">
          <CardHeader className="pb-0">
            <Row>
              <Col md={9}>
                <h4>{heading} User Details</h4>
              </Col>
              <Col md={3} className="d-grid d-md-flex justify-content-md-end d-sm-block">
                <Button variant="outline-primary" className="btn-sm" onClick={handleBack}>
                  Back
                </Button>
              </Col>
            </Row>
          </CardHeader>
          <CardBody className="px-0 pt-0 pb-2">
            <Form onSubmit={handleSubmit(onSubmit)} className="p-3">

              <Row className="mb-3">
                <input type="hidden" {...register("userId")} />

                <Form.Group controlId="firstName" className="col-md-6 col-sm-12">
                  <Form.Label>
                    Company Name<span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control type="text" placeholder="Company Name" {...register("firstName", { required: "Company name is required." })} />
                  <span className="text-xs text-danger">{errors.firstName?.message}</span>
                </Form.Group>

                <Form.Group controlId="lastName" className="col-md-6 col-sm-12">
                  <Form.Label>
                    Contact Person Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control type="text" placeholder="Contact Person Name" {...register("lastName", { required: "Contact person name is required." })} />
                  <span className="text-sm text-danger">{errors.lastName?.message}</span>
                </Form.Group>
              </Row>

              <Row className="mb-3">
                <Form.Group controlId="loginId" className="col-md-6 col-sm-12">
                  <Form.Label>
                    Login Id <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control type="text" placeholder="Login Id" {...register("loginId", {required: "Login Id is required.", minLength: { value: 6, message: "Login Id must be at least 6 characters." }, maxLength: { value: 255, message: "Maximum 255 characters allowed." }, pattern: { value: /^[a-zA-Z0-9_-]+$/, message: "Login Id can only contain letters, numbers, underscores(_), or hyphens(-).",}, validate: (value) => validateLoginId(value, getValues("userId")), })} />
                  <span className="text-sm text-danger">{errors.loginId?.message}</span>
                </Form.Group>

                <Form.Group controlId="email" className="col-md-6 col-sm-12">
                  <Form.Label>
                    Email Id <span className="text-danger">*</span>
                  </Form.Label>
                  {/* <Form.Control type="text" placeholder="Email Id" {...register("email", { required: "Email Id is required.", pattern: { value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, message: "Enter a valid email address.", }, })} /> */}
                  <Form.Control type="email" placeholder="Email Id" {...register("email", {required: "Email Id is required.", maxLength: { value: 255, message: "Maximum 255 characters allowed." }, pattern: {value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, message: "Enter a valid email address.",}, validate: (value) => validateEmail(value, getValues("userId")),})} />
                  <span className="text-sm text-danger">{errors.email?.message}</span>
                </Form.Group>
              </Row>

              <Row className="mb-3">
                <Form.Group controlId="contact" className="col-md-6 col-sm-12">
                  <Form.Label>
                    Contact No. <span className="text-danger">*</span>
                  </Form.Label>
                  {/* <Form.Control type="text" placeholder="Contact No." {...register("contact", { required: "Contact no. is required.", pattern: { value: /^[0-9]{10}$/, message: "Enter a valid 10-digit contact number.", }, })} /> */}
                  <Form.Control type="text" placeholder="Contact No." {...register("contact", {required: "Contact no. is required.", maxLength: { value: 50, message: "Maximum 50 characters allowed." }, pattern: {value: /^[0-9]{10}$/, message: "Enter a valid 10-digit contact number.", }, validate: (value) => validateContact(value, getValues("userId")), })} />
                  <span className="text-sm text-danger">{errors.contact?.message}</span>
                </Form.Group>

                <Form.Group controlId="password" className="col-md-6 col-sm-12">
                  <Form.Label>
                    Password <span className="text-danger">*</span>
                  </Form.Label>
                  <div className="password-wrapper">
                    <Form.Control
                      type={passwordVisible ? "text" : "password"}
                      placeholder="Password"
                      {...register("password", {
                        required: "Password required.",
                        pattern: {
                          value: /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                          message: "Password must be at least 8 characters long, include at least one uppercase letter, one special character, and one alphanumeric character.",
                        },
                      })}
                    />
                    <span
                      className="eye-icon"
                      onMouseDown={onPasswordMouseDown}
                      onMouseUp={onPasswordMouseUp}
                    >
                      {passwordVisible ? '👁️' : '👁️‍🗨️'}
                    </span>
                  </div>
                  <span className="text-sm text-danger">{errors.password?.message}</span>
                </Form.Group>

                {/* <Form.Group controlId="confirmPassword" className="col-md-6 col-sm-12">
                  <Form.Label>
                    Confirm Password <span className="text-danger">*</span>
                  </Form.Label>
                  <div className="password-wrapper">
                    <Form.Control
                      type={passwordVisible ? "text" : "password"}
                      placeholder="Confirm Password"
                      {...register("confirmPassword", {
                        required: "Confirm Password required.",
                        validate: value => value === watch('password') || "Passwords do not match.",
                      })}
                    />
                    <span
                      className="eye-icon"
                      onMouseDown={onPasswordMouseDown}
                      onMouseUp={onPasswordMouseUp}
                    >
                      {passwordVisible ? '👁️' : '👁️‍🗨️'}
                    </span>
                  </div>
                  <span className="text-sm text-danger">{errors.confirmPassword?.message}</span>
                </Form.Group> */}

              </Row>

              <Row className="mb-3">
                <Form.Group controlId="roleId" className="col-md-6 col-sm-12">
                  <Form.Label>
                    Role <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select {...register("roleId", { required: "Role is required." })}>
                    <option value="">Select Role</option>
                    {roles.map((role) => (
                      <option key={role.roleId} value={role.roleId}>
                        {role.roleName}
                      </option>
                    ))}
                  </Form.Select>
                  <span className="text-sm text-danger">{errors.roleId?.message}</span>
                </Form.Group>

                <Form.Group controlId="isActive" className="col-md-6 col-sm-12">
                  <Form.Label>Is Active</Form.Label>
                  <Form.Check type="switch" {...register("isActive")} />
                </Form.Group>
              </Row>

              <Row>
                <Col md={12} className="d-grid gap-2 d-md-flex flex-md-row-reverse">
                  <Button variant="primary" className="me-md-2" type="submit" disabled={isLoading}>
                    {isLoading ? "Saving..." : "Submit"}
                  </Button>
                  <Button variant="dark" type="button" onClick={handleCancel}>
                    Cancel
                  </Button>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
}

export default AddUser;
